import { useWebSocket } from '@app:providers';
import { ProductionOrder, ProductionRequestStatusColorMap, ProductionRequestStatusEnum } from '@app:types';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  Icon,
  Spacer,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { RiCalendarCloseLine, RiCalendarEventLine, RiEdit2Line, RiLoopRightLine } from 'react-icons/ri';
import { formatDate, prettifyObjectKey } from 'utils/helpers/formatting.helpers';
import { FormulationContainer } from './formulation-container';

export function OrderDetailsDrawer({ order, isOpen, onClose }: { order: ProductionOrder; isOpen: boolean; onClose: () => void }) {
  const { send } = useWebSocket();

  return (
    <Drawer isOpen={isOpen} placement='right' size='xl' onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent py={3}>
        <DrawerCloseButton />
        <DrawerBody>
          <Heading as='h1' size='xl'>
            #{order.trackingNumber}
          </Heading>
          Order details
          <Divider my={6} />
          <Heading as='h2' size='lg' mb={4}>
            Items
          </Heading>
          <Accordion allowToggle allowMultiple>
            {order.content.map((request, i) => {
              const isInProduction =
                request.status !== ProductionRequestStatusEnum.CANCELLED && request.status !== ProductionRequestStatusEnum.ERROR;
              return (
                <AccordionItem key={request.id} bgColor='gray.50' mt={i > 0 ? 1 : 0} border={'0px'}>
                  <h2>
                    <AccordionButton>
                      <Text mr={3} fontWeight={'bold'}>
                        {request.template.label} ({request.expected.weight}g)
                      </Text>
                      <Badge variant='outline' colorScheme={ProductionRequestStatusColorMap[request.status]}>
                        {request.status}
                      </Badge>
                      <Spacer />
                      <Flex gap={2} alignItems={'center'}>
                        <Icon as={RiLoopRightLine} />
                        <span>{formatDate(request.updatedAt, 'dateTime')}</span>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  {request.status === ProductionRequestStatusEnum.ERROR && (
                    <Alert status='error'>
                      <AlertIcon />
                      <AlertTitle>Error during product formulation.</AlertTitle>
                      <AlertDescription>{request.errorReason}</AlertDescription>
                    </Alert>
                  )}
                  {request.status === ProductionRequestStatusEnum.CANCELLED && (
                    <Alert status='warning'>
                      <AlertIcon />
                      <AlertTitle>Product was cancelled during production.</AlertTitle>
                      <AlertDescription>{request.cancelReason}</AlertDescription>
                    </Alert>
                  )}
                  <AccordionPanel pb={4}>
                    <ButtonGroup my={3}>
                      {isInProduction && (
                        <Tooltip
                          hasArrow
                          label='Will remove product from production.'
                          bg='red.600'
                          isDisabled={request.status === ProductionRequestStatusEnum.IN_PRODUCTION}
                        >
                          <Button
                            leftIcon={<RiCalendarCloseLine />}
                            colorScheme='red'
                            isDisabled={request.status === ProductionRequestStatusEnum.IN_PRODUCTION}
                            onClick={send.bind(null, {
                              type: 'user.deleteTask',
                              payload: {
                                requestId: request.id,
                              },
                            })}
                          >
                            Cancel
                          </Button>
                        </Tooltip>
                      )}
                      {!isInProduction && (
                        <Tooltip hasArrow label='Will submit product to production.' bg='blue.600'>
                          <Button
                            leftIcon={<RiCalendarEventLine />}
                            colorScheme='blue'
                            onClick={send.bind(null, {
                              type: 'user.retryTask',
                              payload: {
                                requestId: request.id,
                              },
                            })}
                          >
                            Re-try
                          </Button>
                        </Tooltip>
                      )}
                      <Button leftIcon={<RiEdit2Line />} isDisabled={request.status === ProductionRequestStatusEnum.IN_PRODUCTION}>
                        Edit
                      </Button>
                    </ButtonGroup>
                    <Heading as='h3' size='sm' color={'black'} fontWeight={'bold'} mb={3}>
                      Attributes
                    </Heading>
                    <VStack spacing={4} mb={6}>
                      {Object.entries(request.customizationAttributes || {}).map(([label, value]) => {
                        return (
                          <Flex key={label} w='100%' gap={3} borderRadius={3} alignItems={'center'}>
                            <Box as={'span'} flex={2}>
                              {prettifyObjectKey(label)}
                            </Box>
                            <Box as={'span'} flex={5}>
                              <Tag>{value}</Tag>
                            </Box>
                          </Flex>
                        );
                      })}
                    </VStack>
                    <Heading as='h3' size='sm' color={'black'} fontWeight={'bold'} mb={3}>
                      Expected Formulation
                    </Heading>
                    <FormulationContainer
                      formulation={request.expected.formulation}
                      mb={request.status === ProductionRequestStatusEnum.COMPLETED ? 6 : 0}
                    />
                    {request.status === ProductionRequestStatusEnum.COMPLETED && (
                      <>
                        <Heading as='h3' size='sm' color={'black'} fontWeight={'bold'} mb={3}>
                          Actual Formulation
                        </Heading>
                        <FormulationContainer formulation={request.actual.formulation} />
                      </>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
          <Divider my={6} />
          <VStack>
            <Flex w='100%' mt={3} gap={3} borderRadius={3} alignItems={'center'}>
              <Box as={'span'} flex={1}>
                Internal ID
              </Box>
              <Box as={'span'} flex={3}>
                <Tag>{order.id}</Tag>
              </Box>
            </Flex>
            <Flex w='100%' mt={3} gap={3} borderRadius={3} alignItems={'center'}>
              <Box as={'span'} flex={1}>
                Created at
              </Box>
              <Box as={'span'} flex={3}>
                <Tag>{formatDate(order.createdAt, 'dateTime')}</Tag>
              </Box>
            </Flex>
            {Object.entries(order.metadata).map(([label, value]) => {
              return (
                <Flex key={label} w='100%' mt={3} gap={3} borderRadius={3} alignItems={'center'}>
                  <Box as={'span'} flex={1}>
                    {prettifyObjectKey(label)}
                  </Box>
                  <Box as={'span'} flex={3}>
                    <Tag>{value}</Tag>
                  </Box>
                </Flex>
              );
            })}
          </VStack>
          <Divider my={6} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
